/**
 * Behavior Tracker - Self-hosted visitor behavior analytics
 * Captures: scroll depth, time on page, clicks, screen resolution, engagement
 * Sends data to /api/analytics/track-behavior on page unload
 */

const SESSION_KEY = 'lumi_session_id';
const VISITOR_KEY = 'lumi_returning';

// Get or create persistent session ID
function getSessionId(): string {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
        sid = 'v_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
        sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
}

// Check if new or returning visitor
function isNewVisitor(): boolean {
    const hasVisited = localStorage.getItem(VISITOR_KEY);
    if (!hasVisited) {
        localStorage.setItem(VISITOR_KEY, 'true');
        return true;
    }
    return false;
}

interface ClickData {
    element: string;
    x: number;
    y: number;
    timestamp: string;
}

class BehaviorTracker {
    private sessionId: string;
    private currentPage: string = '';
    private startTime: number = 0;
    private maxScrollDepth: number = 0;
    private clicks: ClickData[] = [];
    private isTracking: boolean = false;
    private scrollHandler: (() => void) | null = null;
    private clickHandler: ((e: MouseEvent) => void) | null = null;

    constructor() {
        this.sessionId = getSessionId();
    }

    /**
     * Start tracking a new page. Call this on every route change.
     */
    startPage(pageUrl: string): void {
        // Flush previous page data
        if (this.isTracking && this.currentPage) {
            this.flush();
        }

        this.currentPage = pageUrl;
        this.startTime = Date.now();
        this.maxScrollDepth = 0;
        this.clicks = [];
        this.isTracking = true;

        // Track scroll
        this.scrollHandler = () => {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const docHeight = Math.max(
                document.documentElement.scrollHeight,
                document.body.scrollHeight
            ) - window.innerHeight;
            const depth = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 100;
            this.maxScrollDepth = Math.max(this.maxScrollDepth, depth);
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });

        // Track clicks (sample up to 30)
        this.clickHandler = (e: MouseEvent) => {
            if (this.clicks.length >= 30) return;
            const target = e.target as HTMLElement;
            if (!target) return;
            const element = target.tagName.toLowerCase() +
                (target.id ? `#${target.id}` : '') +
                (target.className && typeof target.className === 'string' ? `.${target.className.split(' ')[0]}` : '');
            this.clicks.push({
                element: element.substring(0, 100),
                x: Math.round(e.clientX),
                y: Math.round(e.clientY),
                timestamp: new Date().toISOString()
            });
        };
        document.addEventListener('click', this.clickHandler, { passive: true });

        // Flush on page hide (works on mobile and tab switching)
        window.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('pagehide', this.handlePageHide);
    }

    private handleVisibilityChange = (): void => {
        if (document.visibilityState === 'hidden') {
            this.flush();
        }
    };

    private handlePageHide = (): void => {
        this.flush();
    };

    /**
     * Send behavior data to the backend
     */
    private flush(): void {
        if (!this.isTracking || !this.currentPage) return;

        const timeOnPage = Math.round((Date.now() - this.startTime) / 1000);
        const engagementScore = this.calcEngagement(timeOnPage);

        const payload = {
            sessionId: this.sessionId,
            pageUrl: this.currentPage,
            scrollDepth: this.maxScrollDepth,
            timeOnPage,
            clicks: this.clicks,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            engagementScore
        };

        // Use sendBeacon for reliable delivery on page unload
        try {
            const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
            navigator.sendBeacon('/api/analytics/track-behavior', blob);
        } catch {
            // Fallback: fire-and-forget fetch
            fetch('/api/analytics/track-behavior', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: true
            }).catch(() => {/* silent */ });
        }
    }

    /**
     * Calculate engagement score (0-100) from multiple signals
     */
    private calcEngagement(timeOnPage: number): number {
        // Time component: max 40 points (caps at 120s)
        const timePts = Math.min(40, (timeOnPage / 120) * 40);
        // Scroll component: max 30 points
        const scrollPts = (this.maxScrollDepth / 100) * 30;
        // Click component: max 30 points (caps at 10 clicks)
        const clickPts = Math.min(30, (this.clicks.length / 10) * 30);
        return Math.round(timePts + scrollPts + clickPts);
    }

    /**
     * Track the initial page view (POST to /api/analytics/track)
     */
    trackPageView(pageUrl: string, language: string = 'sv'): void {
        const payload = {
            sessionId: this.sessionId,
            pageUrl,
            referrer: document.referrer,
            language,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            isNewVisitor: isNewVisitor()
        };

        fetch('/api/analytics/track', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        }).catch(() => {/* silent */ });
    }

    /**
     * Stop tracking and clean up event listeners
     */
    stopPage(): void {
        this.flush();
        this.cleanup();
    }

    private cleanup(): void {
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
            this.scrollHandler = null;
        }
        if (this.clickHandler) {
            document.removeEventListener('click', this.clickHandler);
            this.clickHandler = null;
        }
        window.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.removeEventListener('pagehide', this.handlePageHide);
        this.isTracking = false;
    }

    getSessionId(): string {
        return this.sessionId;
    }
}

// Singleton
export const behaviorTracker = new BehaviorTracker();
export default behaviorTracker;
